import { IconMenu2, IconSearch, IconUser } from '@tabler/icons-react';
import { FC, useEffect, useRef, useState } from 'react';
import MultiStageSearch from '../../../components/MultiStageSearch';
import { SearchAction } from '../../../reducers/searchReducer';
import { useNavigate } from 'react-router-dom';
import { LogoutOptions, useAuth0 } from '../../../auth/AuthProvider';
import useCreateUser, { UserInput } from '../../../hooks/useCreateUser';
import useGetUser from '../../../hooks/useGetUser';
import { Menu, MenuItem } from '@mui/material';

interface Props {
  destination: string;
  month: string;
  diver: number;
  setIsShowDrawer: (isShowDrawer: boolean) => void;
  setShowSearchOverlay: (showSearchOverlay: boolean) => void;
  dispatch: React.Dispatch<SearchAction>;
  currentYear: number;
  searchType: number;
  monthId: number;
  destinationId: number;
  currentStage: number;
}

const SearchBar: FC<Props> = ({
  destination,
  month,
  diver,
  setIsShowDrawer,
  setShowSearchOverlay,
  dispatch,
  currentYear,
  searchType,
  monthId,
  destinationId,
  currentStage,
}) => {
  const [showSearch, setShowSearch] = useState(false);
  const [maxHeight, setMaxHeight] = useState('0px');
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth0();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { user: userData, isLoading: isUserLoading } = useGetUser(
    user?.sub ?? '',
    isAuthenticated && !!user?.sub,
  );
  const createUserMutation = useCreateUser();
  const postLoginActionCompletedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const diverLabel = `${diver} ${diver === 1 ? 'diver' : 'divers'}`;

  const handleToggleSearch = (nextStage: number) => {
    setShowSearch(!showSearch);
    setMaxHeight(showSearch ? '0px' : `100vh`);
    dispatch({ type: 'SET_CURRENT_STAGE', payload: nextStage });
  };

  const handleLogin = () => {
    localStorage.setItem(
      'redirectAfterLogin',
      window.location.pathname + window.location.search,
    );
    navigate('/login');
  };

  useEffect(() => {
    if (
      isAuthenticated &&
      !postLoginActionCompletedRef.current &&
      !isUserLoading &&
      (!userData || userData.auth0Id !== (user?.sub ?? ''))
    ) {
      postLoginActionCompletedRef.current = true;
      const newUser: UserInput = {
        userId: 0,
        auth0Id: user?.sub ?? '',
        fullName: user?.name ?? '',
        email: user?.email ?? '',
        gender: user?.gender ?? '',
        phone: user?.phone_number ?? '',
        specialRequest: '',
      };

      createUserMutation.mutate(newUser, {
        onSuccess: () => {},
        onError: () => {},
      });
    }
  }, [isAuthenticated, isUserLoading, userData, user, createUserMutation]);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout({ returnTo: window.location.origin } as LogoutOptions);
    handleClose();
  };

  useEffect(() => {
    setMaxHeight(showSearch ? `100vh` : '0px');
  }, [showSearch]);

  return (
    <header className="relative z-30 mx-6 my-4 lg:mx-[150px]">
      <div className="flex items-center gap-3 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:items-center">
        <button
          type="button"
          className="flex min-h-11 min-w-11 items-center justify-center lg:hidden"
          onClick={() => setIsShowDrawer(true)}
          aria-label="Open menu"
        >
          <IconMenu2 />
        </button>
        <img
          className="hidden h-8 cursor-pointer justify-self-start lg:block"
          src="https://imagedelivery.net/KQLaZdBjDzxarhwBRuqRFg/33a0d3dd-4713-44fc-2792-1237db489400/public"
          alt="santtai-logo"
          onClick={() => navigate('/')}
        />

        <div
          className="flex w-full items-center justify-between rounded-full bg-white py-2 pl-6 pr-2 shadow-[0px_1px_4px_0px_rgba(0,0,0,0.1)] lg:hidden"
          onClick={() => setShowSearchOverlay(true)}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setShowSearchOverlay(true);
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Edit search"
        >
          <div>
            <div className="text-sm font-semibold text-neutral-800">
              {destination}
            </div>
            <div className="mt-1 text-xs text-neutral-300">
              {month} | {diverLabel}
            </div>
          </div>
          <span
            className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-santtai-primary text-white"
            aria-hidden="true"
          >
            <IconSearch size={16} />
          </span>
        </div>

        <div className="hidden justify-self-center lg:block">
          <div
            className={`flex items-center rounded-full border border-neutral-100 bg-white ${
              showSearch ? 'invisible' : 'visible'
            }`}
          >
            <button
              type="button"
              className="border-r border-neutral-100 px-6 py-4 text-sm font-semibold text-neutral-800"
              onClick={() => handleToggleSearch(1)}
            >
              {destination}
            </button>
            <button
              type="button"
              className="border-r border-neutral-100 px-6 py-4 text-sm font-semibold text-neutral-800"
              onClick={() => handleToggleSearch(2)}
            >
              {month}
            </button>
            <button
              type="button"
              className="flex items-center gap-4 py-2 pl-6 pr-2 text-sm font-semibold text-neutral-800"
              onClick={() => handleToggleSearch(3)}
            >
              {diverLabel}
              <span
                className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-santtai-primary text-neutral-00"
                aria-label="Search"
              >
                <IconSearch size={16} />
              </span>
            </button>
          </div>
        </div>

        <div className="hidden items-center justify-self-end gap-4 lg:flex">
          <button
            type="button"
            className="flex h-11 items-center gap-2 rounded-full border border-neutral-100 px-3"
            onClick={isAuthenticated ? handleMenu : handleLogin}
            aria-label={isAuthenticated ? 'Account menu' : 'Log in'}
            aria-haspopup="true"
            aria-expanded={open}
            id="account-menu-button"
          >
            <IconMenu2 size={20} />
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-neutral-50 text-neutral-800">
              <IconUser size={18} />
            </span>
          </button>
          <Menu
            id="account-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            slotProps={{
              list: {
                'aria-labelledby': 'account-menu-button',
              },
            }}
          >
            <MenuItem
              onClick={() => {
                navigate('/profile');
                handleClose();
              }}
            >
              Edit Profile
            </MenuItem>
            <MenuItem
              onClick={() => {
                navigate('/app/tripmanagement');
                handleClose();
              }}
            >
              My trips
            </MenuItem>
            <MenuItem onClick={handleLogout}>Log Out</MenuItem>
          </Menu>
        </div>
      </div>
      <div
        ref={containerRef}
        className="hidden overflow-hidden transition-all duration-500 ease-in-out lg:block"
        style={{ maxHeight, opacity: showSearch ? '1' : '0' }}
      >
        <MultiStageSearch
          setShowSearchOverlay={setShowSearchOverlay}
          diver={diver}
          destination={destination}
          month={month}
          currentYear={currentYear}
          currentStage={currentStage}
          destinationId={destinationId}
          monthId={monthId}
          searchType={searchType}
          dispatch={dispatch}
          onClose={() => setShowSearch(false)}
          showPopOverForce={showSearch}
        />
      </div>
    </header>
  );
};

export default SearchBar;
